/**
 * 이미지 좌표 계산 유틸리티
 * 이미지의 픽셀 좌표와 서버에서 받은 polygon 좌표 간의 변환 및 계산을 담당
 * 계산 전략을 쉽게 변경할 수 있도록 설계됨
 */

import { coordinateUtils, imagePropertyUtils } from './polygonToImageCalculator.js';

const EARTH_RADIUS = 6371000; // 지구 반지름 (미터)

// 계산 전략 인터페이스
class CalculationStrategy {
  calculateImageProperties(polygonCoordinates, imageSize) {
    throw new Error('calculateImageProperties must be implemented');
  }
}

// 기본 계산 전략 (현재 방식)
class DefaultCalculationStrategy extends CalculationStrategy {
  calculateImageProperties(polygonCoordinates, imageSize) {
    return imagePropertyUtils.calculateImageProperties(polygonCoordinates, imageSize);
  }
}

// 평행사변형 대응 계산 전략
class ParallelogramCalculationStrategy extends CalculationStrategy {
  calculateImageProperties(polygonCoordinates, imageSize) {
    // 평행사변형 대응 로직을 여기에 구현
    // 현재는 기본 전략과 동일하지만, 나중에 다른 로직으로 쉽게 변경 가능
    return imagePropertyUtils.calculateImageProperties(polygonCoordinates, imageSize);
  }
}

// 실제 가로세로 길이 기반 계산 전략 (모든 사각형에 통용)
class ActualDimensionsCalculationStrategy extends CalculationStrategy {
  calculateImageProperties(polygonCoordinates, imageSize) {
    if (!polygonCoordinates || !imageSize) {
      console.error('[ActualDimensionsCalculationStrategy] 유효하지 않은 입력 데이터');
      return null;
    }

    console.log('[ActualDimensionsCalculationStrategy] 실제 가로세로 길이 기반 이미지 속성 계산 시작');

    // 실제 가로세로 길이 기반 스케일 계산
    const scale = this.calculateScaleByActualDimensions(polygonCoordinates, imageSize);

    // 중심점 계산 (기존 방식 사용)
    const center = imagePropertyUtils.calculateImageCenter(polygonCoordinates);

    // 회전각 계산 (기존 방식 사용)
    const rotation = imagePropertyUtils.calculateImageRotation(polygonCoordinates);

    const properties = {
      center: center,
      rotation: rotation,
      scale: scale
    };

    console.log('[AreaBasedCalculationStrategy] 면적 기반 계산 완료:', {
      center: center,
      rotation: rotation,
      scale: scale
    });

    return properties;
  }

  /**
   * 실제 가로세로 길이 기반 스케일 계산
   * @param {Array} polygonCoordinates - 폴리곤 좌표 배열
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @returns {Array} 스케일 [scaleX, scaleY]
   */
  calculateScaleByActualDimensions(polygonCoordinates, imageSize) {
    if (!polygonCoordinates || !imageSize) {
      return [1, 1];
    }

    // 실제 폴리곤 크기 계산 (벡터 기반)
    const actualDimensions = this.calculateVectorBasedPolygonDimensions(polygonCoordinates);

    if (!actualDimensions || !actualDimensions.width || !actualDimensions.height) {
      console.warn('[ActualDimensionsCalculationStrategy] 실제 폴리곤 크기 계산 실패');
      return [1, 1];
    }

    const polygonWidth = actualDimensions.width;
    const polygonHeight = actualDimensions.height;

    if (polygonWidth <= 0 || polygonHeight <= 0) {
      console.warn('[ActualDimensionsCalculationStrategy] 폴리곤 크기가 0 이하입니다.');
      return [1, 1];
    }

    // 실제 가로세로 비율로 스케일 계산
    const scaleX = polygonWidth / imageSize.width;
    const scaleY = polygonHeight / imageSize.height;

    // 🔍 이미지 비율 디버깅 - 스케일 계산
    console.log('🔍 [ActualDimensionsCalculationStrategy] 실제 가로세로 길이 기반 스케일 계산:', {
      polygonWidth: polygonWidth,
      polygonHeight: polygonHeight,
      imageSize: imageSize,
      scaleX: scaleX,
      scaleY: scaleY,
      scaleRatio: scaleX / scaleY,
      scaleRatioRounded: Math.round((scaleX / scaleY) * 1000) / 1000,
      polygonAspectRatio: polygonWidth / polygonHeight,
      imageAspectRatio: imageSize.width / imageSize.height,
      aspectRatioMatch: Math.abs((polygonWidth / polygonHeight) - (imageSize.width / imageSize.height)) < 0.01
    });

    return [scaleX, scaleY]; // 실제 가로세로 비율 유지
  }

  /**
   * 벡터 기반 폴리곤 크기 계산 (긴쪽을 width, 짧은 쪽을 height로 설정)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 실제 크기 {width, height}
   */
  calculateVectorBasedPolygonDimensions(coordinates) {

    // 두 개의 평행한 변 벡터 계산
    const vector1 = [
      coordinates[1][0] - coordinates[0][0],
      coordinates[1][1] - coordinates[0][1]
    ];
    const vector2 = [
      coordinates[3][0] - coordinates[0][0],
      coordinates[3][1] - coordinates[0][1]
    ];

    // 벡터의 크기 계산 (실제 길이)
    const length1 = Math.sqrt(vector1[0] * vector1[0] + vector1[1] * vector1[1]);
    const length2 = Math.sqrt(vector2[0] * vector2[0] + vector2[1] * vector2[1]);

    // 긴 쪽을 width로 설정
    const width = Math.max(length1, length2);
    const height = Math.min(length1, length2);

    // 🔍 이미지 비율 디버깅 - 폴리곤 크기 계산
    console.log('🔍 [ActualDimensionsCalculationStrategy] 벡터 기반 폴리곤 크기 계산:', {
      coordinates: coordinates.length,
      vector1: vector1,
      vector2: vector2,
      length1: length1,
      length2: length2,
      width: width, // 긴 쪽
      height: height, // 짧은 쪽
      aspectRatio: width / height,
      aspectRatioRounded: Math.round((width / height) * 1000) / 1000
    });

    return { width, height };
  }

  /**
   * 경계 상자 계산
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {Object} 경계 상자 {minX, maxX, minY, maxY}
   */
  calculateBoundingBox(coordinates) {
    if (!coordinates || coordinates.length === 0) {
      return null;
    }

    const xs = coordinates.map(coord => coord[0]);
    const ys = coordinates.map(coord => coord[1]);

    return {
      minX: Math.min(...xs),
      maxX: Math.max(...xs),
      minY: Math.min(...ys),
      maxY: Math.max(...ys)
    };
  }

  /**
   * 폴리곤 면적 계산 (사선 공식)
   * @param {Array} coordinates - 폴리곤 좌표 배열
   * @returns {number} 면적
   */
  calculatePolygonArea(coordinates) {
    if (!coordinates || coordinates.length < 3) {
      return 0;
    }

    let area = 0;
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n;
      area += coordinates[i][0] * coordinates[j][1];
      area -= coordinates[j][0] * coordinates[i][1];
    }

    return Math.abs(area) / 2;
  }
}

class ImageCoordinateCalculator {
  constructor(strategy = 'actual-dimensions') {  // 기본 전략을 'actual-dimensions'로 변경
    this.setCalculationStrategy(strategy);
  }

  /**
   * 계산 전략 설정
   * @param {string} strategy - 'default' | 'parallelogram' | 'actual-dimensions' | 'custom'
   */
  setCalculationStrategy(strategy) {
    switch (strategy) {
      case 'default':
        this.calculationStrategy = new DefaultCalculationStrategy();
        break;
      case 'parallelogram':
        this.calculationStrategy = new ParallelogramCalculationStrategy();
        break;
      case 'actual-dimensions':
        this.calculationStrategy = new ActualDimensionsCalculationStrategy();
        break;
      default:
        this.calculationStrategy = new DefaultCalculationStrategy();
        console.warn(`[ImageCoordinateCalculator] 알 수 없는 전략: ${strategy}, 기본 전략 사용`);
    }

    console.log(`[ImageCoordinateCalculator] 계산 전략 변경: ${strategy}`);
  }

  /**
   * 커스텀 계산 전략 설정
   * @param {CalculationStrategy} customStrategy - 커스텀 계산 전략 인스턴스
   */
  setCustomCalculationStrategy(customStrategy) {
    if (customStrategy instanceof CalculationStrategy) {
      this.calculationStrategy = customStrategy;
      console.log('[ImageCoordinateCalculator] 커스텀 계산 전략 설정됨');
    } else {
      console.error('[ImageCoordinateCalculator] 유효하지 않은 계산 전략');
    }
  }

  /**
   * 이미지의 픽셀 좌표를 지리 좌표로 변환
   * @param {Object} imageData - 이미지 데이터
   * @param {number} pixelX - 픽셀 X 좌표
   * @param {number} pixelY - 픽셀 Y 좌표
   * @param {Array} polygonCoordinates - 서버에서 받은 polygon 좌표들
   * @returns {Array} 지리 좌표 [longitude, latitude]
   */
  pixelToGeographic(imageData, pixelX, pixelY, polygonCoordinates) {
    if (!imageData || !polygonCoordinates || polygonCoordinates.length < 3) {
      console.error('[ImageCoordinateCalculator] 유효하지 않은 입력 데이터');
      return null;
    }

    const { width, height } = imageData;

    // 이미지의 4개 모서리 좌표 추출
    const corners = coordinateUtils.extractImageCorners(polygonCoordinates);
    if (!corners) {
      console.error('[ImageCoordinateCalculator] 이미지 모서리 좌표 추출 실패');
      return null;
    }

    // 픽셀 좌표를 0-1 범위로 정규화
    const normalizedX = pixelX / width;
    const normalizedY = pixelY / height;

    // 정규화된 좌표를 지리 좌표로 변환
    return this.interpolateGeographicCoordinate(corners, normalizedX, normalizedY);
  }

  /**
   * 지리 좌표를 이미지 픽셀 좌표로 역변환 (bilinear 보간의 수치 역산).
   * @param {Object} imageData - { width, height }
   * @param {number} lon
   * @param {number} lat
   * @param {Array} polygonCoordinates - 이미지 footprint 링 좌표
   * @returns {{ pixelX: number, pixelY: number, normalizedX: number, normalizedY: number } | null}
   */
  geographicToPixel(imageData, lon, lat, polygonCoordinates) {
    if (!imageData || !polygonCoordinates || polygonCoordinates.length < 3) {
      return null;
    }

    const corners = coordinateUtils.extractImageCorners(polygonCoordinates);
    if (!corners) {
      return null;
    }

    const { width, height } = imageData;
    let nx = 0.5;
    let ny = 0.5;

    for (let i = 0; i < 40; i++) {
      const [gLng, gLat] = this.interpolateGeographicCoordinate(corners, nx, ny);
      const errLng = lon - gLng;
      const errLat = lat - gLat;
      if (Math.abs(errLng) < 1e-11 && Math.abs(errLat) < 1e-11) {
        break;
      }

      const eps = 1e-5;
      const [gLngNx, gLatNx] = this.interpolateGeographicCoordinate(corners, nx + eps, ny);
      const [gLngNy, gLatNy] = this.interpolateGeographicCoordinate(corners, nx, ny + eps);
      const dLngDnx = (gLngNx - gLng) / eps;
      const dLatDnx = (gLatNx - gLat) / eps;
      const dLngDny = (gLngNy - gLng) / eps;
      const dLatDny = (gLatNy - gLat) / eps;
      const det = dLngDnx * dLatDny - dLngDny * dLatDnx;
      if (Math.abs(det) < 1e-18) {
        break;
      }

      const dNx = (errLng * dLatDny - errLat * dLngDny) / det;
      const dNy = (dLngDnx * errLat - dLatDnx * errLng) / det;
      nx = Math.min(1, Math.max(0, nx + dNx));
      ny = Math.min(1, Math.max(0, ny + dNy));
    }

    return {
      pixelX: nx * width,
      pixelY: ny * height,
      normalizedX: nx,
      normalizedY: ny
    };
  }


  /**
   * 정규화된 좌표를 지리 좌표로 보간
   * @param {Object} corners - 4개 모서리 좌표
   * @param {number} normalizedX - 정규화된 X 좌표 (0-1)
   * @param {number} normalizedY - 정규화된 Y 좌표 (0-1)
   * @returns {Array} 지리 좌표 [longitude, latitude]
   */
  interpolateGeographicCoordinate(corners, normalizedX, normalizedY) {
    const { topLeft, topRight, bottomRight, bottomLeft } = corners;

    // 상단 경계에서의 보간
    const topLng = topLeft[0] + (topRight[0] - topLeft[0]) * normalizedX;
    const topLat = topLeft[1] + (topRight[1] - topLeft[1]) * normalizedX;

    // 하단 경계에서의 보간
    const bottomLng = bottomLeft[0] + (bottomRight[0] - bottomLeft[0]) * normalizedX;
    const bottomLat = bottomLeft[1] + (bottomRight[1] - bottomLeft[1]) * normalizedX;

    // 수직 보간
    const finalLng = topLng + (bottomLng - topLng) * normalizedY;
    const finalLat = topLat + (bottomLat - topLat) * normalizedY;

    return [finalLng, finalLat];
  }

  /**
   * OpenLayers Geometry에서 중심점 계산
   * @param {ol/geom/Geometry} geometry - OpenLayers Geometry 객체
   * @returns {Object} 중심점 정보 {center: [x, y], extent: [minX, minY, maxX, maxY]}
   */
  calculateGeometryCenter(geometry) {
    if (!geometry) {
      console.error('[ImageCoordinateCalculator] 유효하지 않은 geometry');
      return null;
    }

    const extent = geometry.getExtent();
    const center = [
      (extent[0] + extent[2]) / 2,
      (extent[1] + extent[3]) / 2
    ];

    return {
      center: center,
      extent: extent
    };
  }

  /**
   * 폴리곤의 최소 반지름 계산 (중심점에서 가장 가까운 꼭지점까지의 거리)
   * @param {ol/geom/Geometry} geometry - OpenLayers Geometry 객체
   * @param {Array} center - 중심점 [x, y]
   * @returns {number} 최소 반지름
   */
  calculateMinRadius(geometry, center) {
    if (!geometry || !center) {
      return 0;
    }

    const coordinates = geometry.getCoordinates()[0];
    let minRadius = Infinity;

    coordinates.forEach(coord => {
      const distance = Math.sqrt(
        Math.pow(coord[0] - center[0], 2) +
        Math.pow(coord[1] - center[1], 2)
      );
      if (distance < minRadius) {
        minRadius = distance;
      }
    });

    return minRadius;
  }

  /**
   * 폴리곤의 스케일 계산 (중심점 기반)
   * @param {ol/geom/Geometry} originalGeometry - 원본 폴리곤 Geometry
   * @param {ol/geom/Geometry} currentGeometry - 현재 폴리곤 Geometry
   * @returns {number} 스케일 비율
   */
  calculatePolygonScale(originalGeometry, currentGeometry) {
    if (!originalGeometry || !currentGeometry) {
      return 1;
    }

    const originalCenter = this.calculateGeometryCenter(originalGeometry).center;
    const currentCenter = this.calculateGeometryCenter(currentGeometry).center;

    // 원본 폴리곤의 중심점에서 첫 번째 꼭지점까지의 거리
    const originalCoords = originalGeometry.getCoordinates()[0];
    const originalDistance = Math.sqrt(
      Math.pow(originalCoords[0][0] - originalCenter[0], 2) +
      Math.pow(originalCoords[0][1] - originalCenter[1], 2)
    );

    // 현재 폴리곤의 중심점에서 첫 번째 꼭지점까지의 거리
    const currentCoords = currentGeometry.getCoordinates()[0];
    const currentDistance = Math.sqrt(
      Math.pow(currentCoords[0][0] - currentCenter[0], 2) +
      Math.pow(currentCoords[0][1] - currentCenter[1], 2)
    );

    const scale = currentDistance / originalDistance;

    console.log('[ImageCoordinateCalculator] calculatePolygonScale 디버깅:', {
      originalCenter: originalCenter,
      currentCenter: currentCenter,
      originalDistance: originalDistance,
      currentDistance: currentDistance,
      scale: scale
    });

    return scale;
  }

  /**
   * 폴리곤의 회전 각도 계산
   * @param {ol/geom/Geometry} geometry - OpenLayers Geometry 객체
   * @returns {number} 회전 각도 (라디안)
   */
  calculatePolygonRotation(geometry) {
    if (!geometry) {
      return 0;
    }

    const coordinates = geometry.getCoordinates();
    if (!coordinates || !coordinates[0] || coordinates[0].length < 2) {
      console.warn('[ImageCoordinateCalculator] 유효하지 않은 좌표 데이터');
      return 0;
    }

    const coordsArray = coordinates[0];
    const p1 = coordsArray[0];
    const p2 = coordsArray[1];

    if (!p1 || !p2 || p1.length < 2 || p2.length < 2) {
      console.warn('[ImageCoordinateCalculator] 유효하지 않은 좌표 점 데이터');
      return 0;
    }

    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const rotationAngle = -Math.atan2(dy, dx);

    return rotationAngle;
  }

  /**
   * GeoImage 동기화를 위한 center, scale, rotation 계산
   * @param {ol/geom/Geometry} geometry - 현재 폴리곤 Geometry
   * @param {ol/geom/Geometry} originalGeometry - 원본 폴리곤 Geometry
   * @param {Object} originalPolygonSize - 원본 폴리곤 크기 {width, height}
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @returns {Object} GeoImage 정보 {center, scale, rotation}
   */
  calculateGeoImageProperties(geometry, originalGeometry, originalPolygonSize, imageSize) {
    if (!geometry || !originalGeometry || !originalPolygonSize || !imageSize) {
      console.error('[ImageCoordinateCalculator] 유효하지 않은 입력 데이터');
      return null;
    }

    // 현재 폴리곤의 중심점 계산
    const centerInfo = this.calculateGeometryCenter(geometry);
    const newCenter = centerInfo.center;

    // 폴리곤의 스케일 계산
    const currentScale = this.calculatePolygonScale(originalGeometry, geometry);

    // 원본 폴리곤 크기에 스케일 적용
    const scaledWidth = originalPolygonSize.width * currentScale;
    const scaledHeight = originalPolygonSize.height * currentScale;

    // 이미지 스케일 계산 (일반 매칭: 폴리곤 width → 이미지 width, 폴리곤 height → 이미지 height)
    const newScaleX = scaledWidth / imageSize.width;
    const newScaleY = scaledHeight / imageSize.height;

    // 폴리곤의 회전 각도 계산
    const rotationAngle = this.calculatePolygonRotation(geometry);


    return {
      center: newCenter,
      scale: [newScaleX, newScaleY],
      rotation: rotationAngle
    };
  }

  /**
   * 폴리곤 좌표로부터 이미지 속성 계산 (전략 패턴 사용)
   * @param {Array} polygonCoordinates - 폴리곤 좌표 배열
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @returns {Object} 이미지 속성 {center, rotation, scale}
   */
  calculateImagePropertiesFromPolygon(polygonCoordinates, imageSize) {
    if (!this.calculationStrategy) {
      console.error('[ImageCoordinateCalculator] 계산 전략이 설정되지 않음');
      return null;
    }

    console.log('[ImageCoordinateCalculator] 폴리곤 좌표로부터 이미지 속성 계산:', {
      strategy: this.calculationStrategy.constructor.name,
      polygonCoordinates: polygonCoordinates.length,
      imageSize: imageSize
    });

    return this.calculationStrategy.calculateImageProperties(polygonCoordinates, imageSize);
  }

  /**
   * 평행사변형의 크기에 맞는 이미지 크기를 결정하기 위한 반지름 계산
   * @param {Array} point - 계산할 점의 좌표 [x, y]
   * @param {Array} center - 중심점 좌표 [x, y]
   * @returns {Object} 반지름 정보 {radius, angle}
   *
   * @example
   * const radiusInfo = calculator.calculateRadiusFromPoint([100, 200], [50, 50]);
   * console.log(radiusInfo.radius); // 158.11
   * console.log(radiusInfo.angle);  // 1.3258 (라디안)
   */
  calculateRadiusFromPoint(point, center) {
    if (!point || !center) {
      return null;
    }

    const dx = point[0] - center[0];
    const dy = point[1] - center[1];
    const radius = Math.sqrt(dx * dx + dy * dy);
    const angle = Math.atan2(dy, dx);

    return {
      radius: radius,
      angle: angle
    };
  }

  /**
   * 평행사변형 변형을 위한 반지름 계산 (초기 설정 및 변형 시 동일한 로직)
   * @param {Array} point - 계산할 점의 좌표 [x, y]
   * @param {Array} center - 중심점 좌표 [x, y]
   * @param {number} minRadius - 최소 반지름 (선택사항)
   * @returns {Object} 반지름 정보 {radius, angle, isValid}
   *
   * @example
   * // 초기 설정 시
   * const initialRadius = calculator.calculateParallelogramRadius(initialPoint, center, 0);
   *
   * // 변형 시
   * const currentRadius = calculator.calculateParallelogramRadius(currentPoint, center, minRadius);
   * if (currentRadius.isValid) {
   *   const scaleRatio = currentRadius.radius / initialRadius.radius;
   *   const rotationAngle = currentRadius.angle - initialRadius.angle;
   * }
   */
  calculateParallelogramRadius(point, center, minRadius = 0) {
    const radiusInfo = this.calculateRadiusFromPoint(point, center);

    if (!radiusInfo) {
      return { radius: 0, angle: 0, isValid: false };
    }

    const isValid = radiusInfo.radius > minRadius;

    return {
      radius: radiusInfo.radius,
      angle: radiusInfo.angle,
      isValid: isValid
    };
  }

  /**
   * Modify 변형 계산 (평행사변형 반지름 계산 함수 사용)
   * @param {Array} point - 현재 마우스 위치 [x, y]
   * @param {Array} modifyPoint - 초기 마우스 위치 [x, y]
   * @param {Array} center - 중심점 [x, y]
   * @param {number} minRadius - 최소 반지름
   * @returns {Object} 변형 정보 {scaleRatio, rotationAngle}
   */
  calculateModifyTransformation(point, modifyPoint, center, minRadius) {
    if (!point || !modifyPoint || !center) {
      return null;
    }

    // 초기 반지름과 각도 계산 (평행사변형 반지름 계산 함수 사용)
    const initialRadiusInfo = this.calculateParallelogramRadius(modifyPoint, center, minRadius);

    if (!initialRadiusInfo.isValid) {
      return null;
    }

    // 현재 반지름과 각도 계산 (평행사변형 반지름 계산 함수 사용)
    const currentRadiusInfo = this.calculateParallelogramRadius(point, center, 0);

    if (!currentRadiusInfo.isValid) {
      return null;
    }

    // 스케일과 회전 계산
    const scaleRatio = currentRadiusInfo.radius / initialRadiusInfo.radius;
    const rotationAngle = currentRadiusInfo.angle - initialRadiusInfo.angle;

    return {
      scaleRatio: scaleRatio,
      rotationAngle: rotationAngle
    };
  }

  /**
   * Geometry에 변형 적용
   * @param {ol/geom/Geometry} geometry - 적용할 Geometry
   * @param {number} scaleRatio - 스케일 비율
   * @param {number} rotationAngle - 회전 각도 (라디안)
   * @param {Array} center - 중심점 [x, y]
   * @returns {ol/geom/Geometry} 변형된 Geometry
   */
  applyTransformationToGeometry(geometry, scaleRatio, rotationAngle, center) {
    if (!geometry || !center) {
      return geometry;
    }

    const transformedGeometry = geometry.clone();

    // 중심점을 기준으로 확대/축소
    transformedGeometry.scale(scaleRatio, undefined, center);

    // 중심점을 기준으로 회전
    transformedGeometry.rotate(rotationAngle, center);

    return transformedGeometry;
  }

  /**
   * 이미지 크기에 맞는 폴리곤 크기 계산
   * @param {Object} imageSize - 이미지 크기 {width, height}
   * @param {Array} center - 중심점 [x, y]
   * @param {number} baseHeight - 기본 세로 크기
   * @returns {Object} 폴리곤 정보 {coordinates, size}
   */
  calculatePolygonSizeFromImage(imageSize, center, baseHeight = 100000) {
    if (!imageSize || !center) {
      return null;
    }

    const imageWidth = imageSize.width;
    const imageHeight = imageSize.height;
    const imageAspectRatio = imageWidth / imageHeight;

    // 반대 매칭을 고려한 폴리곤 생성
    const polygonWidth = baseHeight * (imageHeight / imageWidth);
    const polygonHeight = baseHeight;

    const halfWidth = polygonWidth / 2;
    const halfHeight = polygonHeight / 2;

    // 사각형 폴리곤 좌표 생성
    const coordinates = [
      [center[0] - halfWidth, center[1] - halfHeight], // 좌하단
      [center[0] + halfWidth, center[1] - halfHeight], // 우하단
      [center[0] + halfWidth, center[1] + halfHeight], // 우상단
      [center[0] - halfWidth, center[1] + halfHeight], // 좌상단
      [center[0] - halfWidth, center[1] - halfHeight]  // 시작점으로 돌아가기
    ];

    return {
      coordinates: coordinates,
      size: {
        width: polygonWidth,
        height: polygonHeight
      }
    };
  }
}

// 싱글톤 인스턴스 생성
const imageCoordinateCalculator = new ImageCoordinateCalculator();

export default imageCoordinateCalculator;

/**
 * 사용 예시:
 *
 * 1. 실제 가로세로 길이 기반 전략 사용 (기본값 - 모든 사각형에 통용)
 * const calculator = new ImageCoordinateCalculator('actual-dimensions');
 *
 * 2. 기존 방식 사용 (직사각형에 최적화)
 * const calculator = new ImageCoordinateCalculator('default');
 *
 * 3. 평행사변형 대응 전략 사용
 * const calculator = new ImageCoordinateCalculator('parallelogram');
 *
 * 4. 런타임에 전략 변경
 * calculator.setCalculationStrategy('actual-dimensions');
 *
 * 5. 커스텀 전략 구현 및 사용
 * class CustomCalculationStrategy extends CalculationStrategy {
 *   calculateImageProperties(polygonCoordinates, imageSize) {
 *     // 커스텀 로직 구현
 *     return { center: [0, 0], rotation: 0, scale: [1, 1] };
 *   }
 * }
 * calculator.setCustomCalculationStrategy(new CustomCalculationStrategy());
 *
 * 6. 폴리곤 좌표로부터 이미지 속성 계산
 * const properties = calculator.calculateImagePropertiesFromPolygon(polygonCoordinates, imageSize);
 *
 * 7. 픽셀-지리 좌표 변환
 * const geographicCoord = calculator.pixelToGeographic(imageData, pixelX, pixelY, polygonCoordinates);
 *
 * 8. OpenLayers Geometry 기반 계산 (이미지 속성 계산)
 * const geoImageProps = calculator.calculateGeoImageProperties(geometry, originalGeometry, originalPolygonSize, imageSize);
 */
